/**
 * Plot share token API — FXC-8720.
 *
 * Mint, revoke, and resolve anonymous read-only share tokens for a single plot.
 * All scoped read endpoints (data, flowfield, chat) authenticate via the raw
 * token in the query string — NO Authorization header needed.
 *
 * CONTRACT (mirrors app/routes/plot_share.py):
 *
 *   POST /api/plots/share          — mint (authenticated)
 *   GET  /api/plots/share/resolve  — resolve token → scope (anonymous)
 *   GET  /api/plots/share/<plot_id>/data?token=<raw>      — fetch plot data
 *   GET  /api/plots/share/<plot_id>/flowfield?token=<raw> — (FXC-8721 stub)
 *   GET  /api/plots/share/<plot_id>/chat?token=<raw>      — (FXC-8722 stub)
 *   DELETE /api/plots/share/<token_id>                    — revoke (authenticated)
 */

import { apiClient, unauthenticatedApiClient } from "./client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The minimal scope a share token grants — this is all the token reveals. */
export interface PlotShareScope {
  plot_id: string;
  project_id: string;
  branch: string;
  allowed_points: string[] | null;
  /** Whether this share is allowed to fetch flowfield data (default false). */
  flowfield_enabled?: boolean;
}

export interface PlotShareMintRequest {
  plot_id: string;
  project_id: string;
  branch?: string;
  allowed_points?: string[] | null;
  expires_in_hours?: number;
  label?: string;
  /** Opt in to flowfield access for this share (heavy data pull; default off). */
  flowfield_enabled?: boolean;
}

export interface PlotShareMintResponse {
  token: string;
  token_prefix: string;
  share_url: string;
  scope: PlotShareScope;
  expires_at: string | null;
}

export interface PlotShareRecord {
  id: string;
  token_prefix: string;
  plot_id: string;
  project_id: string;
  branch: string;
  allowed_points: string[] | null;
  flowfield_enabled?: boolean;
  label: string | null;
  created_by: string;
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
}

export interface SharedPlotDataResponse {
  plot_id: string;
  project_id: string;
  branch: string;
  allowed_points: string[] | null;
  plot: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Mint (authenticated)
// ---------------------------------------------------------------------------

/**
 * Mint a new share token for exactly one plot.
 * Requires the caller to be authenticated with a full-scope token or JWT.
 */
export async function mintPlotShareToken(
  req: PlotShareMintRequest,
): Promise<PlotShareMintResponse> {
  return apiClient<PlotShareMintResponse>("/plots/share", {
    method: "POST",
    body: req,
  });
}

// ---------------------------------------------------------------------------
// List tokens for a plot (authenticated)
// ---------------------------------------------------------------------------

export async function listPlotShareTokens(
  plotId: string,
): Promise<PlotShareRecord[]> {
  return apiClient<PlotShareRecord[]>(`/plots/share?plot_id=${plotId}`);
}

// ---------------------------------------------------------------------------
// Revoke (authenticated)
// ---------------------------------------------------------------------------

export async function revokePlotShareToken(tokenId: string): Promise<void> {
  await apiClient(`/plots/share/${tokenId}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Flowfield pre-bake trigger (FXC-8721 dependency)
// ---------------------------------------------------------------------------

/**
 * Trigger pre-bake of flowfield data for the given share token's allowed points.
 *
 * FXC-8721 stub contract: POST /api/plots/share/<plot_id>/flowfield/prebake
 *
 * This is a fire-and-forget optimistic call — if FXC-8721 hasn't landed yet,
 * the endpoint returns 501 and we silently discard the error. The caller
 * (PlotShareModal) documents this dependency with a visible UI note.
 *
 * When FXC-8721 merges, this endpoint will enqueue the background bake job
 * and return 202 Accepted. The frontend UI does not need to change.
 */
export async function triggerFlowfieldPreBake(
  plotId: string,
  tokenId: string,
  allowedPoints: string[] | null,
): Promise<{ accepted: boolean }> {
  try {
    await apiClient<void>(`/plots/share/${plotId}/flowfield/prebake`, {
      method: "POST",
      body: { token_id: tokenId, allowed_points: allowedPoints },
    });
    return { accepted: true };
  } catch {
    // 501 = FXC-8721 not yet merged; swallow silently.
    return { accepted: false };
  }
}

// ---------------------------------------------------------------------------
// Anonymous resolve — validate token and get scope (no auth header)
// ---------------------------------------------------------------------------

/**
 * Resolve a raw share token to its scope.
 * This is an anonymous endpoint — no Authorization header is sent.
 * Returns null if the token is invalid, expired, or revoked.
 */
export async function resolvePlotShareToken(
  rawToken: string,
): Promise<PlotShareScope | null> {
  try {
    return await unauthenticatedApiClient<PlotShareScope>(
      `/plots/share/resolve?token=${encodeURIComponent(rawToken)}`,
    );
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Scoped data fetch (anonymous — token in query string)
// ---------------------------------------------------------------------------

/**
 * Fetch plot data for the ONE plot bound to this token.
 * No Authorization header — the token IS the credential.
 */
export async function fetchSharedPlotData(
  plotId: string,
  rawToken: string,
): Promise<SharedPlotDataResponse> {
  return unauthenticatedApiClient<SharedPlotDataResponse>(
    `/plots/share/${plotId}/data?token=${encodeURIComponent(rawToken)}`,
  );
}
